import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { resolveFileLink } from 'utils/FileUploadAPI.ts';

type FileThumbnailProps = {
  /** Files API paths for this file, in the order to try them (`fileLinkURIs`). */
  uris: string[];
  authToken: string;
  /** Called with the path whose image loaded, so a download can start from it. */
  onLoad?: (uri: string) => void;
};

const THUMBNAIL_SIZE = '48px';

/**
 * Image preview for one file row. The Files API serves the image only behind a
 * short-lived link, because an `<img src>` cannot send the Authorization header.
 *
 *   mount ──▶ resolveFileLink(uris[attempt]) ──┬── https link ──▶ <img> ──load──▶ preview
 *                                              │                    └── error ──┐
 *                                              └── failure ─────────────────────┴──▶ next path
 *                                                     (the legacy palmettodev copy), or the
 *                                                     ImageIcon placeholder once none is left
 */
function FileThumbnail({ uris, authToken, onLoad }: FileThumbnailProps) {
  const theme = useTheme();
  const [attempt, setAttempt] = useState(0);
  // *: Tagged with the attempt it came from, so a link from a path that already
  // *: failed never renders while the next path resolves.
  const [resolved, setResolved] = useState<{ attempt: number; url: string } | null>(null);

  useEffect(() => {
    // *: happy: the link resolves -> <img> renders it.
    // *: nil/null: no path left to try -> the placeholder stays.
    // *: empty/zero: empty authToken -> resolveFileLink rejects before any request.
    // *: upstream error: non-2xx / timeout / no https url -> the next path.
    // *: unmount: a link that settles after the row is gone is ignored.
    if (attempt >= uris.length) return;
    let cancelled = false;
    resolveFileLink(uris[attempt], authToken).then(
      (url) => {
        if (!cancelled) setResolved({ attempt, url });
      },
      (error) => {
        if (cancelled) return;
        console.error('🚀 FileThumbnail preview link failed: ', error);
        setAttempt(attempt + 1);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [attempt, uris, authToken]);

  if (!resolved || resolved.attempt !== attempt) {
    return (
      <ImageIcon
        sx={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE, color: theme.palette.grey[400] }}
      />
    );
  }

  return (
    <img
      src={resolved.url}
      // *: Decorative: the file name is already shown beside it.
      alt=''
      loading='lazy'
      style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE, objectFit: 'contain' }}
      onLoad={() => onLoad?.(uris[attempt])}
      onError={() => {
        // *: The link resolved but its image did not load (removed, expired, not an
        // *: image). The link stays out of the log: it is a credential for the file.
        console.error('🚀 FileThumbnail preview image failed to load');
        setAttempt(attempt + 1);
      }}
    />
  );
}

export default FileThumbnail;
